import { Injectable, Provider } from '@angular/core';

import { ICON_SETTINGS, IconSettings, IconSettingsService } from '@progress/kendo-angular-icons';
import { SVGIcon, calendarIcon, minusIcon, plusIcon, searchIcon, xCircleIcon, xIcon } from '@progress/kendo-svg-icons';

/**
 * Kendo's v5 icon set redesigned every glyph from filled to thin outline and defaults to the
 * outline variant, which is why carets render as chevrons and everything looks faint. Selecting
 * the variant on ICON_SETTINGS restores the pre-upgrade look for the whole app.
 *
 * Providing the token replaces Kendo's default `{ type: 'svg' }` wholesale, so the type has to
 * be repeated or no icon renders at all.
 */
export const KENDO_ICON_SETTINGS: IconSettings = { type: 'svg', variant: 'solid' };

/**
 * Icons that read better as outlines than as filled blocks, keyed by the name Kendo looks them
 * up under. That is usually the icon's own name, but components are free to use an alias — the
 * dialog and window title bars bind xIcon while asking for "close" — and a name we do not cover
 * falls through to the raw icon, which the app-wide solid variant then fills in.
 */
const OUTLINE_ICONS: Record<string, SVGIcon> = {
  [calendarIcon.name]: calendarIcon,
  // the grid detail toggle, filled to a solid block otherwise; minus keeps the expanded state
  // looking like the collapsed one
  [plusIcon.name]: plusIcon,
  [minusIcon.name]: minusIcon,
  [searchIcon.name]: searchIcon,
  [xIcon.name]: xIcon,
  [xCircleIcon.name]: xCircleIcon,
  close: xIcon,
};

/**
 * The outline variants are drawn at the SVG default stroke width of 1 in a 24 unit viewBox,
 * which renders faint at the size adornment icons are displayed at.
 */
const OUTLINE_STROKE_WIDTH = 2;

@Injectable()
export class KendoIconSettingsService extends IconSettingsService {
  // The variants map has to be dropped: the icon renders `variants[variant] || content`, so
  // leaving it in place would let the app-wide variant win again.
  private readonly outlineOverrides: Record<string, SVGIcon> = Object.entries(OUTLINE_ICONS).reduce(
    (map, [name, icon]) => {
      map[name] = {
        name: icon.name,
        viewBox: icon.viewBox,
        content: (icon.variants?.['outline'] ?? icon.content).replace(
          /<path /g,
          `<path stroke-width="${OUTLINE_STROKE_WIDTH}" `,
        ),
      };

      return map;
    },
    {} as Record<string, SVGIcon>,
  );

  public override getSvgIcon(name: string, component?: string): SVGIcon {
    return this.outlineOverrides[name] ?? super.getSvgIcon(name, component);
  }
}

/** Kendo icon configuration shared by every app. */
export function provideKendoIconSettings(): Provider[] {
  return [
    { provide: ICON_SETTINGS, useValue: KENDO_ICON_SETTINGS },
    { provide: IconSettingsService, useClass: KendoIconSettingsService },
  ];
}
